import { clamp } from './cosmicMath';

export interface WheelColor {
  name: string;
  hex: `#${string}`;
  rgb: `${number}, ${number}, ${number}`;
}

export interface WheelSpinPlan {
  targetIndex: number;
  finalRotationDegrees: number;
  durationMs: number;
}

export const wheelColors: WheelColor[] = [
  { name: 'solar red', hex: '#ff3b30', rgb: '255, 59, 48' },
  { name: 'ember orange', hex: '#ff7a00', rgb: '255, 122, 0' },
  { name: 'gold flare', hex: '#ffd166', rgb: '255, 209, 102' },
  { name: 'acid lime', hex: '#b8ff2c', rgb: '184, 255, 44' },
  { name: 'aurora green', hex: '#2cff7a', rgb: '44, 255, 122' },
  { name: 'mint beam', hex: '#53ffd6', rgb: '83, 255, 214' },
  { name: 'cyan plasma', hex: '#69f7ff', rgb: '105, 247, 255' },
  { name: 'sky reactor', hex: '#35a7ff', rgb: '53, 167, 255' },
  { name: 'blue nova', hex: '#3d5afe', rgb: '61, 90, 254' },
  { name: 'ultraviolet', hex: '#8f5cff', rgb: '143, 92, 255' },
  { name: 'nebula violet', hex: '#bf5fff', rgb: '191, 95, 255' },
  { name: 'hot magenta', hex: '#ff4fd8', rgb: '255, 79, 216' },
  { name: 'rose laser', hex: '#ff5c93', rgb: '255, 92, 147' },
  { name: 'coral pulse', hex: '#ff6f61', rgb: '255, 111, 97' },
  { name: 'tangerine flash', hex: '#ffb000', rgb: '255, 176, 0' },
  { name: 'lemon arc', hex: '#f5ff45', rgb: '245, 255, 69' },
  { name: 'electric chartreuse', hex: '#7dff00', rgb: '125, 255, 0' },
  { name: 'green signal', hex: '#00e676', rgb: '0, 230, 118' },
  { name: 'teal comet', hex: '#00d4aa', rgb: '0, 212, 170' },
  { name: 'ice blue', hex: '#80d8ff', rgb: '128, 216, 255' },
  { name: 'indigo engine', hex: '#536dfe', rgb: '83, 109, 254' },
  { name: 'purple storm', hex: '#aa00ff', rgb: '170, 0, 255' },
  { name: 'fuchsia bloom', hex: '#ff00aa', rgb: '255, 0, 170' },
  { name: 'crimson afterglow', hex: '#d5003f', rgb: '213, 0, 63' },
];

export function normalizeDegrees(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

export function getWheelSegmentSize(colorCount = wheelColors.length): number {
  return colorCount > 0 ? 360 / colorCount : 360;
}

export function getWheelLandingIndex(finalRotationDegrees: number, colorCount = wheelColors.length): number {
  if (colorCount <= 0) {
    return 0;
  }

  const segmentSize = getWheelSegmentSize(colorCount);
  const pointerAngle = normalizeDegrees(360 - finalRotationDegrees);
  return Math.floor(pointerAngle / segmentSize) % colorCount;
}

export function createWheelSpinPlan(seed: number, currentRotationDegrees = 0, colorCount = wheelColors.length): WheelSpinPlan {
  const safeColorCount = Math.max(1, colorCount);
  const targetIndex = Math.min(Math.floor(clamp(seed, 0, 0.999999) * safeColorCount), safeColorCount - 1);
  const segmentSize = getWheelSegmentSize(safeColorCount);
  const centerOfTarget = targetIndex * segmentSize + segmentSize / 2;
  const rotationRemainder = normalizeDegrees(currentRotationDegrees);
  const targetRemainder = normalizeDegrees(360 - centerOfTarget);
  const deltaToTarget = normalizeDegrees(targetRemainder - rotationRemainder);
  const dramaticTurns = 7 + Math.floor(seed * 5);

  return {
    targetIndex,
    finalRotationDegrees: currentRotationDegrees + dramaticTurns * 360 + deltaToTarget,
    durationMs: 3600 + Math.round(seed * 1400),
  };
}

export function buildWheelGradient(colors = wheelColors): string {
  if (colors.length === 0) {
    return 'conic-gradient(#ffffff, #ffffff)';
  }

  const segmentSize = getWheelSegmentSize(colors.length);
  const stops = colors.flatMap((color, index) => {
    const start = index * segmentSize;
    const end = (index + 1) * segmentSize;
    return [`${color.hex} ${start.toFixed(3)}deg`, `${color.hex} ${end.toFixed(3)}deg`];
  });

  return `conic-gradient(${stops.join(', ')})`;
}
